import { inject, provideAppInitializer } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { LANGUAGE_CODE_SUPPORTED, LocalhostKeys } from '@core/constants';

export const DEFAULT_LANGUAGE = LANGUAGE_CODE_SUPPORTED.en;

const SUPPORTED_LANGUAGES = Object.values(LANGUAGE_CODE_SUPPORTED) as string[];

function isSupported(lang?: string | null): lang is LANGUAGE_CODE_SUPPORTED {
  return !!lang && SUPPORTED_LANGUAGES.includes(lang);
}

/**
 * Resolve a supported UI language WITHOUT an authenticated session: the last
 * language the user picked (persisted to localStorage) -> the browser language
 * -> the default. This is what lets the login page reached after a token expiry
 * render in the user's language instead of always falling back to English.
 */
export function resolveInitialLanguage(): LANGUAGE_CODE_SUPPORTED {
  const stored = localStorage.getItem(LocalhostKeys.LANGUAGE)?.toLowerCase();
  if (isSupported(stored)) {
    return stored;
  }

  const browser = globalThis.navigator?.language?.split('-')[0]?.toLowerCase();
  if (isSupported(browser)) {
    return browser;
  }

  return DEFAULT_LANGUAGE;
}

/**
 * Load i18n translations before the first route renders so EVERY screen — the
 * authenticated app and the unauthenticated login page alike — has its strings
 * ready, with no flash of raw translation keys. Runs at bootstrap, independent
 * of auth state, so an expired token redirecting to the login page is covered.
 *
 * A failed translation fetch must never block the app from booting, so the
 * load is best-effort: on error we still resolve and let ngx-translate fall
 * back to the default language / show keys.
 */
export const provideTranslationInitializer = () =>
  provideAppInitializer(() => {
    const translate = inject(TranslateService);
    translate.setDefaultLang(DEFAULT_LANGUAGE);
    return firstValueFrom(translate.use(resolveInitialLanguage())).catch(() => undefined);
  });
